import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useBookingDispatch,
  useBookingState,
} from "../../hooks/useBookingState";
import { useMoney } from "../../hooks/useMoney";
import { useProdaName } from "../../hooks/useProdaName";
import { deepCopy } from "../../utils/common";
import {
  PRODA_CALENDAR_CONF,
  ProdAttrUtils,
} from "../../utils/productAttributeUtils";
import CheckBox from "../Checkbox";
import { SelectTagArea } from "../detail/SelectTagArea";
import { ProdAttrCalendar } from "./ProdAttrCalendar";

const PICK_LATER = -1;
const proda2OptionValue = (x?: BookingAttributeDTO): number | undefined =>
  x?.productAttribute?.id;

export const ProdAttrCalendarPicker: FC<ProdAttrPickerModeProps> = (props) => {
  const bookingState = useBookingState(props);
  const bookingAttributes = bookingState.bookingAttributes;
  const product = bookingState.product;
  const calendarBaLs = useMemo(
    () =>
      bookingAttributes.filter(
        (x) =>
          PRODA_CALENDAR_CONF[x.productAttribute.name as ProdAttrNameCalendar],
      ),
    [bookingAttributes],
  );
  return (
    <ProdAttrCalendarPickerBody
      key={calendarBaLs.length}
      {...props}
      product={product}
      calendarBaLs={calendarBaLs}
    />
  );
};
const ProdAttrCalendarPickerBody: FC<
  ProdAttrPickerModeProps & {
    calendarBaLs: BookingAttributeDTO[]; // Product attributes for calendar picker
    product?: ProductDetailResponse;
  }
> = ({ calendarBaLs, product, ...props }) => {
  const bookingDispatch = useBookingDispatch(props);
  const bookingState = useBookingState({
    mode: props.mode,
  });
  const { formatMoney } = useMoney();
  const bookingAttributes = bookingState.bookingAttributes;
  const seat = bookingAttributes.find(
    (item) => item.value?.paName === "MUSICAL_3RDPARTY",
  )?.value?.extProductBookingValues?.[0] as any;
  const { t } = useTranslation();
  const { prodaName } = useProdaName();
  const isMusical = product?.productType === "MUSICAL";

  const valuesLs = useMemo(
    () => calendarBaLs.map(proda2OptionValue),
    [calendarBaLs],
  );
  const isDefautPickLater = useMemo(
    () =>
      !props.calendarOnly &&
      calendarBaLs.length &&
      calendarBaLs.every((x) => x.value?.pickLater),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calendarBaLs],
  );

  const defaultSelected = useMemo(
    () => (isDefautPickLater ? PICK_LATER : (valuesLs[0] ?? undefined)),
    [isDefautPickLater, valuesLs],
  );
  const [selected, setSelected] = useState<number | undefined>(defaultSelected);
  useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  // console.log("selected", selected);
  const currentProdAttr: BookingAttributeDTO | undefined = useMemo(() => {
    return calendarBaLs.find((p) => selected === p.productAttribute.id);
  }, [calendarBaLs, selected]);

  useEffect(() => {
    const a0 = calendarBaLs[0];
    if (a0 && !selected) {
      setSelected(proda2OptionValue(a0));
    }
    // return () => {
    //   setSelected(undefined);
    // };
  }, [calendarBaLs, selected]);

  if (!calendarBaLs.length) return null;

  const options: OptionProps[] = [];

  const hasTimeSlot = calendarBaLs.some(
    (x) =>
      PRODA_CALENDAR_CONF[x.productAttribute.name as ProdAttrNameCalendar]
        ?.pickTimeSlot,
  );

  const isPicklater = (function () {
    if (props.calendarOnly) return false;
    if (product?.categories?.meta) {
      try {
        const meta: CateMeta = JSON.parse(product.categories.meta ?? "{}");
        return !meta["SHUTTLE_BUS"];
      } catch (e) {
        console.error("Error parsing meta:", e);
      }
    }
    return calendarBaLs.some((x) => ProdAttrUtils.CALENDAR.isPickLater(x));
  })();

  const isPriorityPick = calendarBaLs.some((x) =>
    (x.productAttribute.name ?? "").startsWith("PRIORITY"),
  );

  const isSim = props.d?.productType === "SIM";
  if (isPicklater) {
    options.push({
      displayName: t("Select date later"),
      value: PICK_LATER,
      group: "DEFAULT",
    });
  }
  calendarBaLs.forEach((x) => {
    options.push({
      displayName: prodaName(x.productAttribute),
      value: proda2OptionValue(x),
      group: (x.productAttribute.name ?? "").startsWith("PRIORITY")
        ? "PRIORITY"
        : "DEFAULT",
      invisible: x.productAttribute.name === "ESIM_CALENDAR",
    });
  });

  options.sort((a, b) => {
    const f = (x: any) => {
      const x1 = x.group === "PRIORITY" ? 10000 : 0;
      const x2 = x.value === PICK_LATER ? 1000 : 0;
      return x1 + x2;
    };
    return f(a) - f(b);
  });

  return (
    <>
      <SelectTagArea
        containerClassName={`${props.mode === "POPUP" ? "lg:grid-cols-2" : ""} grid-cols-2`}
        className={isSim ? "mb-0" : ""}
        options={options}
        defaultSelected={[selected].filter((x) => x)}
        onSelect={(values) => {
          const v = values[0]?.value;
          if (v === PICK_LATER) {
            bookingDispatch.bookingPickLs(
              calendarBaLs.map((x) => {
                return {
                  ...x,
                  value: {
                    pickLater: true,
                    destDate: undefined,
                    destTimeslot: "",
                  },
                };
              }),
            );
          } else {
            bookingDispatch.bookingPickLs(
              calendarBaLs.map((x) => {
                return {
                  ...x,
                  value: {
                    pickLater: false,
                    destDate: undefined,
                    destTimeslot: "",
                  },
                };
              }),
            );
          }

          setSelected(v);
        }}
        title={t(isSim ? "Activation date" : "Select date")}
        priority={isPriorityPick}
        groupNodes={
          !isPriorityPick
            ? undefined
            : {
                DEFAULT: (
                  <CheckBox
                    maxSelectable={1}
                    options={[
                      {
                        displayName: t("Select date"),
                        value: -2,
                        group: "DEFAULT",
                      },
                    ]}
                    classNameProp={{ style: { fontSize: "0.875rem" } }}
                    defaultSelected={[selected === PICK_LATER ? 0 : -2].filter(
                      (x) => x,
                    )}
                    onSelect={() => {
                      bookingDispatch.bookingPickLs(
                        calendarBaLs.map((x) => {
                          x = deepCopy(x);
                          if (x.value?.pickLater) {
                            x.value.pickLater = undefined;
                          }
                          return x;
                        }),
                      );
                      setSelected(proda2OptionValue(calendarBaLs[0]));
                    }}
                  />
                ),
              }
        }
      />
      {selected !== PICK_LATER && (
        <ProdAttrCalendar
          {...props}
          booka={currentProdAttr}
          pickTimeSlot={hasTimeSlot}
        />
      )}
      {seat && isMusical && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2 font-normal text-[#5E6D77]">
            <p className="text-sm">{t("Select seat")}</p>
          </div>
          <div className="flex items-center justify-between gap-2 rounded border border-[#007AFF] bg-[#DBE7FF] px-4 py-2 text-[#007AFF]">
            <p>
              {seat?.ticketType}{" "}
              {`${seat?.row}${seat?.lowSeatNumber}-${seat?.highSeatNumber} ${seat?.area}`}
            </p>
            <p className="min-w-32 shrink-0 border-l border-[#232323] text-center">
              {formatMoney(+seat?.price)}
            </p>
          </div>
        </>
      )}
    </>
  );
};
